import type { PassportSchemaDef } from "../types.js";

/**
 * EARLY-POSITIONING DRAFT — paints & coatings are in the ESPR working plan
 * 2025-2030 and already carry the Deco Paints Directive 2004/42/EC (VOC
 * content limits) + CLP hazard-labelling obligations. Chemical-formulation
 * family (see detergent-v1); the identity + hazards + SDS shape is shared, the
 * composition accent is VOC content (g/L) which is the regulated headline
 * figure. Field ids are STABLE; trued up when the delegated act lands.
 */
export const paintV1: PassportSchemaDef = {
  id: "paint-v1",
  version: "1.0.0-draft",
  title: {
    tr: "Boya ve Kaplama Ürün Pasaportu",
    en: "Paint & Coatings Product Passport",
    de: "Farben- und Beschichtungs-Produktpass",
  },
  jsonLdContext: {
    "@version": 1.1,
    "@vocab": "https://vocab.idpp.net/paint/v1#",
    schema: "https://schema.org/",
    gs1: "https://gs1.org/voc/",
    identifier: "gs1:gtin",
    issuer: "schema:issuedBy",
    issuedAt: "schema:dateCreated",
  },
  categories: [
    {
      id: "identity",
      labels: { tr: "Ürün Kimliği", en: "Product Identity", de: "Produktidentität" },
      fields: [
        {
          id: "identity.productName",
          type: "string",
          required: true,
          tier: "public",
          labels: { tr: "Ürün adı", en: "Product name", de: "Produktname" },
        },
        {
          id: "identity.productDescription",
          type: "string",
          required: false,
          tier: "public",
          labels: { tr: "Ürün özeti", en: "Product summary", de: "Produktbeschreibung" },
          description: "Short description of the product and its intended use.",
          presentation: { kind: "summary" },
        },
        {
          id: "identity.gtin",
          type: "string",
          required: true,
          tier: "public",
          labels: { tr: "GTIN", en: "GTIN", de: "GTIN" },
          description: "GS1 Global Trade Item Number (8/12/13/14 digits).",
        },
        {
          id: "identity.paintType",
          type: "enum",
          required: true,
          tier: "public",
          enumValues: [
            "decorative-wall",
            "wood-coating",
            "metal-coating",
            "primer",
            "varnish-lacquer",
            "industrial",
            "other",
          ],
          labels: { tr: "Boya türü", en: "Paint type", de: "Farbtyp" },
        },
        {
          id: "identity.baseType",
          type: "enum",
          required: true,
          tier: "public",
          enumValues: ["water-based", "solvent-based", "powder", "uv-cured", "other"],
          labels: { tr: "Baz türü", en: "Base type", de: "Basistyp" },
        },
        {
          id: "identity.color",
          type: "string",
          required: false,
          tier: "public",
          labels: { tr: "Renk", en: "Colour", de: "Farbe" },
        },
        {
          id: "identity.finish",
          type: "enum",
          required: false,
          tier: "public",
          enumValues: ["matt", "eggshell", "satin", "semi-gloss", "gloss", "other"],
          labels: { tr: "Yüzey görünümü (parlaklık)", en: "Finish", de: "Glanzgrad" },
        },
        {
          id: "identity.brand",
          type: "string",
          required: true,
          tier: "public",
          labels: { tr: "Marka", en: "Brand", de: "Marke" },
        },
        {
          id: "identity.manufacturerName",
          type: "string",
          required: true,
          tier: "public",
          labels: { tr: "Üretici adı", en: "Manufacturer name", de: "Herstellername" },
        },
        {
          id: "identity.countryOfManufacture",
          type: "country",
          required: true,
          tier: "public",
          labels: { tr: "Üretim ülkesi", en: "Country of manufacture", de: "Herstellungsland" },
          description: "ISO 3166-1 alpha-2 code (e.g. TR).",
        },
        {
          id: "identity.netContent",
          type: "string",
          required: false,
          tier: "public",
          labels: { tr: "Net içerik", en: "Net content", de: "Nettoinhalt" },
          description: "E.g. 2.5 L, 15 kg.",
        },
        {
          id: "identity.batchNumber",
          type: "string",
          required: false,
          tier: "restricted",
          labels: { tr: "Parti numarası", en: "Batch number", de: "Chargennummer" },
        },
      ],
    },
    {
      id: "hazards",
      labels: {
        tr: "Tehlike Sınıflandırması (CLP)",
        en: "Hazard Classification (CLP)",
        de: "Gefahreneinstufung (CLP)",
      },
      fields: [
        {
          id: "hazards.clpSignalWord",
          type: "enum",
          required: true,
          tier: "public",
          enumValues: ["danger", "warning", "none"],
          labels: { tr: "CLP uyarı kelimesi", en: "CLP signal word", de: "CLP-Signalwort" },
          highlight: true,
        },
        {
          id: "hazards.hazardStatements",
          type: "string",
          required: false,
          tier: "public",
          labels: { tr: "Tehlike ifadeleri (H)", en: "Hazard statements (H)", de: "Gefahrenhinweise (H)" },
          description: "H-codes, e.g. H226, H317.",
        },
        {
          id: "hazards.precautionaryStatements",
          type: "string",
          required: false,
          tier: "restricted",
          labels: {
            tr: "Önlem ifadeleri (P)",
            en: "Precautionary statements (P)",
            de: "Sicherheitshinweise (P)",
          },
        },
        {
          id: "hazards.ghsPictograms",
          type: "string",
          required: false,
          tier: "public",
          labels: { tr: "GHS piktogramları", en: "GHS pictograms", de: "GHS-Piktogramme" },
        },
        {
          id: "hazards.flammable",
          type: "boolean",
          required: false,
          tier: "public",
          labels: { tr: "Yanıcı", en: "Flammable", de: "Entzündbar" },
        },
      ],
    },
    {
      id: "composition",
      labels: { tr: "Bileşim", en: "Composition", de: "Zusammensetzung" },
      fields: [
        {
          id: "composition.vocContentGPerL",
          type: "number",
          required: true,
          tier: "public",
          unit: "g/L",
          labels: { tr: "VOC içeriği", en: "VOC content", de: "VOC-Gehalt" },
          description: "Volatile organic compound content — limited by Directive 2004/42/EC.",
          highlight: true,
        },
        {
          id: "composition.vocCategory",
          type: "string",
          required: false,
          tier: "public",
          labels: { tr: "VOC alt kategorisi", en: "VOC subcategory", de: "VOC-Unterkategorie" },
          description: "Directive 2004/42 subcategory (e.g. a/b/d…).",
        },
        {
          id: "composition.binderType",
          type: "string",
          required: false,
          tier: "public",
          labels: { tr: "Bağlayıcı türü", en: "Binder type", de: "Bindemitteltyp" },
        },
        {
          id: "composition.pigmentsSummary",
          type: "string",
          required: false,
          tier: "public",
          labels: { tr: "Pigmentler", en: "Pigments", de: "Pigmente" },
        },
        {
          id: "composition.svhcPresent",
          type: "boolean",
          required: false,
          tier: "public",
          labels: {
            tr: "SVHC içerir (>%0,1 a/a)",
            en: "Contains SVHC (>0.1% w/w)",
            de: "Enthält SVHC (>0,1 % w/w)",
          },
        },
        {
          id: "composition.svhcSubstances",
          type: "string",
          required: false,
          tier: "public",
          labels: { tr: "SVHC madde listesi", en: "SVHC substances", de: "SVHC-Stoffe" },
        },
        {
          id: "composition.heavyMetalsPresent",
          type: "boolean",
          required: false,
          tier: "public",
          labels: { tr: "Ağır metal içerir", en: "Contains heavy metals", de: "Enthält Schwermetalle" },
        },
        {
          id: "composition.biocidesPresent",
          type: "boolean",
          required: false,
          tier: "public",
          labels: { tr: "Biyosit içerir", en: "Contains biocides", de: "Enthält Biozide" },
        },
      ],
    },
    {
      id: "performance",
      labels: { tr: "Performans", en: "Performance", de: "Leistung" },
      fields: [
        {
          id: "performance.standardConformity",
          type: "string",
          required: true,
          tier: "public",
          labels: { tr: "Standart uygunluğu", en: "Standard conformity", de: "Normkonformität" },
          description: "E.g. EN 13300 (interior wall paints classification).",
        },
        {
          id: "performance.coveragePerLiterM2",
          type: "number",
          required: false,
          tier: "public",
          unit: "m²/L",
          labels: { tr: "Litre başına kapama", en: "Coverage per litre", de: "Ergiebigkeit pro Liter" },
          highlight: true,
        },
        {
          id: "performance.dryingTimeHours",
          type: "number",
          required: false,
          tier: "public",
          unit: "h",
          labels: { tr: "Kuruma süresi", en: "Drying time", de: "Trocknungszeit" },
        },
        {
          id: "performance.coatsRecommended",
          type: "number",
          required: false,
          tier: "public",
          labels: { tr: "Önerilen kat sayısı", en: "Coats recommended", de: "Empfohlene Anstriche" },
        },
        {
          id: "performance.durabilityYears",
          type: "number",
          required: false,
          tier: "public",
          unit: "years",
          labels: { tr: "Dayanıklılık", en: "Durability", de: "Haltbarkeit" },
          highlight: true,
        },
      ],
    },
    {
      id: "usage",
      labels: { tr: "Kullanım ve Bertaraf", en: "Usage & Disposal", de: "Verwendung & Entsorgung" },
      fields: [
        {
          id: "usage.applicationMethod",
          type: "string",
          required: true,
          tier: "public",
          labels: { tr: "Uygulama yöntemi", en: "Application method", de: "Auftragsmethode" },
          description: "Brush, roller, spray…",
        },
        {
          id: "usage.surfacePreparation",
          type: "string",
          required: false,
          tier: "public",
          labels: { tr: "Yüzey hazırlığı", en: "Surface preparation", de: "Untergrundvorbereitung" },
        },
        {
          id: "usage.disposalInstructions",
          type: "string",
          required: true,
          tier: "public",
          labels: { tr: "Bertaraf talimatları", en: "Disposal instructions", de: "Entsorgungshinweise" },
        },
        {
          id: "usage.sdsUrl",
          type: "url",
          required: false,
          tier: "restricted",
          labels: {
            tr: "Güvenlik Bilgi Formu (SDS)",
            en: "Safety Data Sheet (SDS)",
            de: "Sicherheitsdatenblatt (SDB)",
          },
        },
        {
          id: "usage.recyclableContainer",
          type: "boolean",
          required: false,
          tier: "public",
          labels: {
            tr: "Geri dönüştürülebilir ambalaj",
            en: "Recyclable container",
            de: "Recycelbarer Behälter",
          },
          highlight: true,
        },
      ],
    },
  ],
};
